import { Textarea } from "@/src/components/ui/textarea";
import { useToast } from "@/src/components/ui/use-toast";
import { useFetch } from "@/src/lib/useFetch";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/src/components/ui/button";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { PostRegionValues, PostRegionSchema } from "../schemas";
import { Label } from "@/src/components/ui/label";
import { RelationSingleInput } from "../components/RelationSingleInput";
import { Country } from "../model";
import { FormErrors } from "../components/FormErrors";

export function AddRegion() {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const navigate = useNavigate();

  const onSubmit = async (data: PostRegionValues) => {
    try {
      const response = await fetch<{ data: { id: string } }>("/api/locations/regions", "POST", data);
      toast({ title: "Success", description: "Region added successfully" });
      navigate(`/locations/regions/${response.data.id}`);
    } catch (error) {
      toast({ title: "Error", description: "Failed to add region", variant: "destructive" });
    }
  };

  return (
    <div className="rounded-2xl shadow-md bg-white p-4">
      <h2 className="text-lg font-semibold">Add Region</h2>
      <EditRegionForm onSubmit={onSubmit} />
    </div>
  );
}

export function EditRegionForm({ onSubmit }: { onSubmit: (data: PostRegionValues) => void }) {
  const { fetch } = useFetch();

  const loadCountries = async (q: string) => {
    const data = await fetch<{ data: Country[] }>(
      `/api/locations/countries?page=1&pageSize=100&filter=${encodeURIComponent(q)}`,
    );
    return data.data;
  };

  const form = useForm<PostRegionValues>({
    resolver: zodResolver(PostRegionSchema),
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = form;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-4">
        <div>
          <Label>Names</Label>
          <Textarea {...register("names")} placeholder="Enter names, one per line" />
        </div>

        <div>
          <Label>Good to Know</Label>
          <Textarea {...register("goodToKnow")} placeholder="Good to know information" />
        </div>

        <div>
          <Label>Notes</Label>
          <Textarea {...register("notes")} placeholder="Notes" />
        </div>

        <RelationSingleInput name="country" label="Country" loadOptions={loadCountries} />

        <FormErrors errors={errors} />

        <Button type="submit">Save</Button>
      </form>
    </FormProvider>
  );
}
